/**
 * ImpersonationBanner — persistent top banner shown when admin is impersonating a user.
 *
 * Highly visible, fixed position, appears on every page.
 * Uses auth context state — not URL params or local UI state.
 */

import React, { useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button, tokens } from '@fluentui/react-components';
import { DismissRegular, EyeRegular } from '@fluentui/react-icons';
import { useAuth } from '../contexts/AuthContext';

const BANNER_HEIGHT = 40;

const ImpersonationBanner: React.FC = () => {
  const navigate = useNavigate();
  const { user, isImpersonating, exitImpersonation, isLoading } = useAuth();

  const handleExitImpersonation = useCallback(async () => {
    const restoredAdminSession = await exitImpersonation();
    if (restoredAdminSession) {
      navigate('/admin', { replace: true });
    }
  }, [exitImpersonation, navigate]);

  if (!isImpersonating || !user?.impersonation) {
    return null;
  }

  return (
    <div
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        height: BANNER_HEIGHT,
        backgroundColor: tokens.colorBrandBackground2,
        color: tokens.colorBrandForeground2,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 12,
        zIndex: 999999,
        fontSize: 13,
        fontWeight: 500,
        borderBottom: `1px solid ${tokens.colorBrandStroke1}`,
        boxShadow: tokens.shadow4,
      }}
    >
      <EyeRegular style={{ fontSize: 16 }} />
      <span>
        ⚠ Viewing as <strong>{user.name || user.email}</strong>
        {' · '}
        {user.email}
        {' · '}
        Read-only impersonation
      </span>
      <Button
        appearance="transparent"
        size="small"
        icon={<DismissRegular />}
        onClick={handleExitImpersonation}
        disabled={isLoading}
        style={{
          color: tokens.colorBrandForeground2,
          border: `1px solid ${tokens.colorBrandStroke1}`,
          marginLeft: 8,
        }}
      >
        Exit impersonation
      </Button>
    </div>
  );
};

/** Height of the banner in px — use to offset app content when impersonating */
export const IMPERSONATION_BANNER_HEIGHT = BANNER_HEIGHT;

export default ImpersonationBanner;